import React, { useState } from "react";
import {
    Form,
    FormItem,
    CardContent,
    Input,
    Textarea,
    Select,
    Option,
    OnOffSwitch,
    FormButtons,
    Button,
    Toast,
    Row,
    Col,
    Image,
    ImageEditor,
    Popup,
    PopupContent,
    ProgressBar,
    Card,
    CardTitle
} from "@tv2no/ui-react";
import { updatePersonList } from "../../../api/services/personList";
import { uploadPersonListImage } from "../../../api/services/personListEntry";
import { resizeFile } from "../../../utils/fileUtils";

const Details = ({ personList, setPersonList, fetchPersonList, personListCategories }) => {
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [busyUpdating, setBusyUpdating] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [showImageEditor, setShowImageEditor] = useState(null);

    /**
     * Save changes
     * @private
     */
    const handleSaveChanges = () => {
        // Busy on
        setBusyUpdating(true);

        // Send request
        updatePersonList(personList.id, {
            name: personList.name,
            description: personList.description,
            categoryId: personList.category.id,
            sorting: personList.sorting,
            published: personList.published
        })
            .then(() => {
                Toast.success("Endringene ble lagret");
                setBusyUpdating(false);
                setUnsavedChanges(false);
            })
            .catch(() => {
                Toast.error("Feil ved lagring");
                setBusyUpdating(false);
            });
    };

    const handleChangeProp = (key, value) => {
        if (key === "category") {
            value = personListCategories.find((c) => c.id === parseInt(value, 10));
        }

        setPersonList({ ...personList, [key]: value });
        setUnsavedChanges(true);
    };

    /**
     * Upload image
     */
    const handleUploadImage = async (file, type) => {
        setShowImageEditor(false);
        if (!file) {
            return;
        }
        setUploadProgress(1);
        const resizedFile = await resizeFile({file, size: type === "THUMB" ? 300 : 1500});
        uploadPersonListImage(personList.id, type, resizedFile, (percent) => {
            setUploadProgress(percent);
        })
            .then((result) => {
                fetchPersonList(() => {
                    setUploadProgress(null);
                });
            })
            .catch((error) => {
                setUploadProgress(null);
                Toast.error("Feil ved opplasting av bilde.");
            });
    };

    return (
        <React.Fragment>
            <Card>
                <CardTitle title="Detaljer" />
                <CardContent>
                    <Row>
                        <Col md={14}>
                            <Form onSubmit={handleSaveChanges}>
                                <Row>
                                    <Col span={24}>
                                        <FormItem label="Name">
                                            <Input
                                                value={personList.name}
                                                onChange={(val) => handleChangeProp("name", val)}
                                                maxLength={50}
                                            />
                                        </FormItem>
                                    </Col>
                                    <Col span={24}>
                                        <FormItem label="Beskrivelse">
                                            <Textarea
                                                value={personList.description}
                                                onChange={(val) =>
                                                    handleChangeProp("description", val)
                                                }
                                            />
                                        </FormItem>
                                    </Col>
                                    <Col span={24}>
                                        <FormItem label="Kategori">
                                            <Select
                                                value={
                                                    personList.category && personList.category.id
                                                }
                                                onChange={(val) =>
                                                    handleChangeProp("category", val)
                                                }
                                            >
                                                {personListCategories &&
                                                    personListCategories.map((cat) => (
                                                        <Option key={cat.id} value={cat.id}>
                                                            {cat.name}
                                                        </Option>
                                                    ))}
                                            </Select>
                                        </FormItem>
                                    </Col>
                                    <Col span={24}>
                                        <FormItem label="Sortering">
                                            <Select
                                                value={personList.sorting}
                                                onChange={(val) => handleChangeProp("sorting", val)}
                                            >
                                                <Option key="INCOME" value="INCOME">
                                                    Inntekt
                                                </Option>
                                                <Option key="FORTUNE" value="FORTUNE">
                                                    Formue
                                                </Option>
                                                <Option key="TAX" value="TAX">
                                                    Skatt
                                                </Option>
                                                <Option key="CUSTOM" value="CUSTOM">
                                                    Rangering
                                                </Option>
                                            </Select>
                                        </FormItem>
                                    </Col>
                                    <Col span={24}>
                                        <FormItem label="Publisert">
                                            <OnOffSwitch
                                                value={personList.published}
                                                onChange={(val) =>
                                                    handleChangeProp("published", val)
                                                }
                                            />
                                        </FormItem>
                                    </Col>
                                    <Col span={24}>
                                        <FormButtons>
                                            <Button
                                                type="submit"
                                                color="primary"
                                                disabled={!unsavedChanges}
                                                busy={busyUpdating}
                                            >
                                                Lagre endringer
                                            </Button>
                                        </FormButtons>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                        <Col md={10}>
                            <Row>
                                <Col span={24}>
                                    <FormItem label="Miniatyrbilde">
                                        <Image
                                            src={personList?.thumbnail?.url}
                                            thumbnail="medium"
                                            onClick={() => setShowImageEditor("THUMB")}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={24}>
                                    <FormItem label="Fullskjermsbilde">
                                        <Image
                                            src={personList?.cover?.url}
                                            scale="16:9"
                                            onClick={() => setShowImageEditor("COVER")}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </CardContent>
            </Card>

            {/* Upload progress */}
            <Popup visible={!!uploadProgress}>
                <PopupContent>
                    <ProgressBar percent={uploadProgress}>Laster opp bildet...</ProgressBar>
                </PopupContent>
            </Popup>

            {!!showImageEditor ? (
                <ImageEditor
                    title={`${personList.name} - Miniatyrbilde`}
                    onClose={() => setShowImageEditor(null)}
                    onSave={(file) => handleUploadImage(file, showImageEditor)}
                />
            ) : null}
        </React.Fragment>
    );
};

export default Details;
